import { ApiError } from '../lib/api'

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : 'http://127.0.0.1:8000/api/v1')

/**
 * Clinical chart API client — encounters + documents.
 *
 * FHIR-aligned read views over the patient chart (ADR-015):
 *
 * - GET /api/v1/patients/{patient_id}/encounters → { items: EncounterView[], total }
 * - GET /api/v1/patients/{patient_id}/encounters/{encounter_id} → EncounterView
 * - GET /api/v1/patients/{patient_id}/documents → { items: DocumentReferenceView[], total }
 * - GET /api/v1/patients/{patient_id}/documents/{document_id} → DocumentReferenceView
 *
 * EncounterView mirrors the FHIR Encounter resource (status, class, type,
 * period, reasonCode/reasonText, participant practitioner, location,
 * hospitalization.dischargeDisposition) plus the encounter's clinical notes.
 * DocumentReferenceView mirrors FHIR DocumentReference (type, description,
 * content.attachment: filename/contentType/size/hash, retention class).
 *
 * No fabricated fallback content: failures throw ApiError and the UI renders
 * honest loading/error/empty states.
 */

/* ------- Encounter (FHIR Encounter-aligned) ------- */

export type EncounterNote = {
  id?: string
  note_type: string
  source: string
  author: string | null
  taken_at: string | null
  title: string | null
  body: string
  signed_status?: string | null
  addendum_of?: string | null
}

/**
 * EncounterView — the shape Dex's encounters router actually returns
 * (apps/api/app/api/v1/encounters.py `_encounter_to_view`).
 *
 * Note vs the original pinned spec: FHIR `Encounter.class` is exposed as
 * `type` (single string code), `resource_type` tags the FHIR family, and the
 * practitioner is a `practitioner_id` reference only (no denormalized name
 * until the Practitioner table lands). The panel renders every field the
 * backend carries and stays honest where the reference is unresolved.
 */
export type EncounterView = {
  id: string
  resource_type?: 'Encounter'
  patient_id?: string
  facility_id?: string
  status: string
  /** FHIR Encounter.class (outpatient | inpatient | emergency | telehealth | home). */
  type: string
  period_start: string | null
  period_end: string | null
  reason_code: string | null
  reason_text: string | null
  practitioner_id: string | null
  /** Present only if/when the backend denormalizes the practitioner name. */
  practitioner_name?: string | null
  location_ward: string | null
  location_clinic: string | null
  discharge_disposition: string | null
  notes: EncounterNote[]
  created_at?: string | null
  updated_at?: string | null
}

export type EncounterListResponse = {
  items: EncounterView[]
  total: number
}

/* ------- Document (FHIR DocumentReference-aligned) ------- */

/**
 * DocumentReferenceView — the shape Dex's documents router actually returns
 * (apps/api/app/api/v1/documents.py `_document_to_view`). The binary body is
 * never returned; `content_status` reports storage-tier presence ('available'
 * today; 'missing'/'expired' probes are a follow-up).
 */
export type DocumentReferenceView = {
  id: string
  resource_type?: 'DocumentReference'
  patient_id?: string
  doc_type: string
  title: string
  description: string | null
  filename: string
  mime_type: string
  size_bytes: number | null
  storage_path?: string | null
  sha256: string | null
  uploaded_by: string | null
  uploaded_at: string | null
  retention_class: string | null
  encounter_id: string | null
  content_status?: string
  created_at?: string | null
  updated_at?: string | null
}

export type DocumentListResponse = {
  items: DocumentReferenceView[]
  total: number
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!response.ok) {
    let detail: unknown = response.statusText
    try {
      const body = await response.json()
      detail = body.detail ?? body
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, detail)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** List a patient's encounters — FHIR Encounter-aligned summary views. */
export async function listPatientEncounters(
  patientId: string,
  options: { signal?: AbortSignal } = {},
): Promise<EncounterListResponse> {
  return requestJson<EncounterListResponse>(`/patients/${patientId}/encounters`, {
    signal: options.signal,
  })
}

/** Full encounter view — includes all clinical notes on the encounter. */
export async function getPatientEncounter(
  patientId: string,
  encounterId: string,
  options: { signal?: AbortSignal } = {},
): Promise<EncounterView> {
  return requestJson<EncounterView>(`/patients/${patientId}/encounters/${encounterId}`, {
    signal: options.signal,
  })
}

/** List a patient's documents — FHIR DocumentReference-aligned metadata. */
export async function listPatientDocuments(
  patientId: string,
  options: { signal?: AbortSignal } = {},
): Promise<DocumentListResponse> {
  return requestJson<DocumentListResponse>(`/patients/${patientId}/documents`, {
    signal: options.signal,
  })
}

/** Full document metadata view (content body fetch is a separate follow-up). */
export async function getPatientDocument(
  patientId: string,
  documentId: string,
  options: { signal?: AbortSignal } = {},
): Promise<DocumentReferenceView> {
  return requestJson<DocumentReferenceView>(`/patients/${patientId}/documents/${documentId}`, {
    signal: options.signal,
  })
}
